import { motion } from 'framer-motion';
import { Star, TrendingUp, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const WHATSAPP_URL = 'https://wa.me/917398073200?text=I%20want%20a%20free%20menu%20audit';

export const Hero = () => {
  const { language, toggleLanguage, t } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden" data-testid="hero-section">
      {/* Background Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-br from-[#F59E0B]/10 to-[#FFB703]/10 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-left"
          >
            {/* Badge & Language Toggle */}
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3 mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <span className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse" />
                <span className="text-sm text-white/70">{t.hero.badge}</span>
              </div>
              <button
                onClick={toggleLanguage}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors duration-200"
                data-testid="language-toggle"
              >
                <span className="text-sm font-medium text-[#FFB703]">
                  {language === 'en' ? 'हिंदी में देखें' : 'View in English'}
                </span>
              </button>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-6"
              data-testid="hero-headline"
            >
              {t.hero.headline}{' '}
              <span className="text-gradient">{t.hero.headlineHighlight}</span>{' '}
              {t.hero.headlineEnd}
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={itemVariants}
              className="text-lg sm:text-xl text-white/60 mb-8 max-w-xl"
              data-testid="hero-subheadline"
            >
              {t.hero.subheadline}{' '}
              <span className="text-[#10B981] font-semibold">{t.hero.price}</span>.{' '}
              {t.hero.delivery} <span className="text-white font-medium">{t.hero.deliveryTime}</span>.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-base px-8 py-4 min-h-[52px] flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                <Zap className="w-5 h-5" />
                {t.hero.ctaPrimary}
              </a>
              <a
                href="#comparison"
                className="px-8 py-4 min-h-[52px] rounded-full bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors duration-200 flex items-center"
                data-testid="hero-cta-secondary"
              >
                {t.hero.ctaSecondary}
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/10">
              <div>
                <div className="text-3xl font-semibold text-white flex items-center gap-1">
                  30%
                  <TrendingUp className="w-5 h-5 text-[#10B981]" />
                </div>
                <div className="text-sm text-white/50">{t.hero.statOrders}</div>
              </div>
              <div>
                <div className="text-3xl font-semibold text-white">24h</div>
                <div className="text-sm text-white/50">{t.hero.statTurnaround}</div>
              </div>
              <div>
                <div className="text-3xl font-semibold text-white flex items-center gap-1">
                  4.9
                  <Star className="w-4 h-4 text-[#FFB703] fill-[#FFB703]" />
                </div>
                <div className="text-sm text-white/50">{t.hero.statRating}</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Floating Zomato Card */}
          <motion.div
            initial={{ opacity: 0, x: 50, rotateY: -10 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
            className="relative hidden lg:block"
          >
            {/* Glow behind card */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#F59E0B]/20 to-[#FFB703]/10 blur-[60px] rounded-3xl" />
            
            {/* Floating Card */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="relative glass rounded-3xl p-6 glow-orange"
              data-testid="hero-floating-card"
            >
              {/* Restaurant Header */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#121212]">
                  <img
                    src="https://images.unsplash.com/photo-1589302168068-964664d93dc0?w=100&h=100&fit=crop"
                    alt="Restaurant"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white">{t.biryaniCard.name}</h3>
                  <p className="text-sm text-white/50">{t.biryaniCard.cuisine}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-1 bg-[#10B981] px-2 py-0.5 rounded text-xs font-medium">
                      <Star className="w-3 h-3 fill-white" />
                      4.5
                    </div>
                    <span className="text-xs text-white/50">{t.biryaniCard.time}</span>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                  <img
                    src="https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?w=60&h=60&fit=crop"
                    alt="Biryani"
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-white">{t.biryaniCard.item1}</h4>
                    <p className="text-xs text-white/50">{t.biryaniCard.item1Desc}</p>
                  </div>
                  <span className="text-sm font-semibold text-[#10B981]">₹299</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                  <img
                    src="https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?w=60&h=60&fit=crop"
                    alt="Butter Chicken"
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-white">{t.biryaniCard.item2}</h4>
                    <p className="text-xs text-white/50">{t.biryaniCard.item2Desc}</p>
                  </div>
                  <span className="text-sm font-semibold text-[#10B981]">₹349</span>
                </div>
              </div>

              {/* Ranking Badge */}
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-[#F59E0B] to-[#FFB703] px-4 py-2 rounded-full shadow-lg">
                <span className="text-sm font-semibold text-[#0d0a07] flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  {t.biryaniCard.ranking}
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
